// src/Component/SideData/Overview/AiAgentWorkFlow/ValidationRulesBuilder.jsx
//
// ============================================================
// VALIDATION RULES BUILDER
// ============================================================
//
// A panel for authoring cross-field validation rules against a
// form. Two rule kinds are supported (matching the backend and
// the client-side evaluator):
//
//   conditional_required  — "if X equals V, then [A, B] are
//                            required."
//   presence_cardinality  — "at least N of [A, B, C] must be
//                            non-empty."
//
// The panel is presentational + callback-driven. It does NOT talk
// to the backend. The host screen owns the fetch and the save and
// passes the current rule array down; the panel calls onChange
// with a new array whenever the admin edits something.
//
// The same panel is mounted in two host screens:
//
//   • MyQNATest.js — the leadgen form editor. The panel sits
//     below the field list. The form's own `uuid` is the
//     form_uuid the rules are keyed on.
//
//   • InferredFieldsBuilder.jsx — the AI-skill field editor. The
//     skill editor resolves the form's uuid through the intent
//     record; the panel doesn't know or care where it came from.
//
// Field name consistency:
//   The `availableFields` prop is a list of `{ field, label }`
//   pairs. `field` is the internal identifier the payload template
//   uses; `label` is what the admin sees. The panel stores `field`
//   values in the rules — never labels. This is the same
//   discipline as `deriveFieldsFromApiConfig` and
//   `handleFAQSubmission` — a rule that references a label would
//   silently fail to match at evaluation time.
//
// Field references are NOT validated against the form's live
// field list at edit time. If the admin later renames or removes a
// field, the rule becomes a no-op at evaluation time (the
// client-side evaluator fails open). The panel shows a warning
// chip next to any rule that references a field not present in
// the current `availableFields` list, so the admin can notice and
// fix it, but the save is never blocked by this.

import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaPlus, FaTrash, FaEdit, FaTimes } from "react-icons/fa";
import styles from "./ValidationRulesBuilder.module.css";

const RULE_KINDS = {
  CONDITIONAL_REQUIRED: "conditional_required",
  PRESENCE_CARDINALITY: "presence_cardinality",
};

const KIND_LABELS = {
  [RULE_KINDS.CONDITIONAL_REQUIRED]: "Require fields when…",
  [RULE_KINDS.PRESENCE_CARDINALITY]: "Require at least N of…",
};

/**
 * Generate a stable rule id. Client-side only; the backend accepts
 * any non-empty string and de-dupes on write. Using a timestamp +
 * random suffix means two rules added in the same millisecond
 * still get distinct ids.
 */
function newRuleId() {
  return `vr_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * Human-readable summary line for the rule list. Long enough to
 * identify the rule at a glance, short enough to fit on one line
 * in the collapsed list.
 */
function summariseRule(rule, fieldsByKey) {
  const labelOf = (key) =>
    fieldsByKey[key]?.label || fieldsByKey[key]?.field || key;

  if (rule.kind === RULE_KINDS.CONDITIONAL_REQUIRED) {
    const whenField = labelOf(rule.when?.field);
    const whenValue = String(rule.when?.equals ?? "");
    const requiredLabels = (rule.require || []).map(labelOf).join(", ");
    return `If ${whenField} = "${whenValue}", require: ${requiredLabels}`;
  }

  if (rule.kind === RULE_KINDS.PRESENCE_CARDINALITY) {
    const ofLabels = (rule.of || []).map(labelOf).join(", ");
    return `At least ${rule.at_least} of: ${ofLabels}`;
  }

  return "(unknown rule)";
}

/**
 * Returns a list of field keys in the rule that don't exist in the
 * current form's field list. The panel surfaces this as a warning
 * chip per rule. Not used to block anything.
 */
function findStaleFieldRefs(rule, availableFieldKeys) {
  const stale = [];
  const check = (key) => {
    if (key && !availableFieldKeys.has(key)) stale.push(key);
  };

  if (rule.kind === RULE_KINDS.CONDITIONAL_REQUIRED) {
    check(rule.when?.field);
    (rule.require || []).forEach(check);
  } else if (rule.kind === RULE_KINDS.PRESENCE_CARDINALITY) {
    (rule.of || []).forEach(check);
  }

  return stale;
}

// ============================================================
// RULE EDITOR MODAL
// ============================================================
//
// Single-step modal (no wizard). The admin picks a kind, fills in
// the kind-specific fields, optionally writes a message, and
// confirms. The modal validates its own shape before calling
// onConfirm — the parent never sees a malformed rule.

function RuleEditorModal({
  isOpen,
  initialRule,
  availableFields,
  onClose,
  onConfirm,
}) {
  const isEditing = !!initialRule;

  const [kind, setKind] = useState(
    initialRule?.kind || RULE_KINDS.CONDITIONAL_REQUIRED,
  );
  const [whenField, setWhenField] = useState(
    initialRule?.when?.field || "",
  );
  const [whenEquals, setWhenEquals] = useState(
    initialRule?.when?.equals ?? "",
  );
  const [requireFields, setRequireFields] = useState(
    initialRule?.require || [],
  );
  const [ofFields, setOfFields] = useState(initialRule?.of || []);
  const [atLeast, setAtLeast] = useState(initialRule?.at_least ?? 1);
  const [message, setMessage] = useState(initialRule?.message || "");
  const [error, setError] = useState("");

  // Reset local state whenever the modal is (re)opened with a
  // different rule. `isOpen` alone isn't enough — the modal is
  // mounted once and reused, so we key on the actual target rule.
  useEffect(() => {
    if (!isOpen) return;
    setKind(initialRule?.kind || RULE_KINDS.CONDITIONAL_REQUIRED);
    setWhenField(initialRule?.when?.field || "");
    setWhenEquals(initialRule?.when?.equals ?? "");
    setRequireFields(initialRule?.require || []);
    setOfFields(initialRule?.of || []);
    setAtLeast(initialRule?.at_least ?? 1);
    setMessage(initialRule?.message || "");
    setError("");
  }, [isOpen, initialRule]);

  if (!isOpen) return null;

  const toggleRequireField = (key) => {
    setRequireFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const toggleOfField = (key) => {
    setOfFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const validate = () => {
    if (kind === RULE_KINDS.CONDITIONAL_REQUIRED) {
      if (!whenField) return "Please pick the condition field.";
      if (whenEquals === "" || whenEquals === null)
        return "Please provide the condition value.";
      if (requireFields.length === 0)
        return "Please pick at least one field to require.";
    }
    if (kind === RULE_KINDS.PRESENCE_CARDINALITY) {
      if (ofFields.length < 2)
        return "Please pick at least two fields.";
      if (!Number.isInteger(atLeast) || atLeast < 1)
        return "At least must be an integer ≥ 1.";
      if (atLeast > ofFields.length)
        return `At least can't exceed the number of fields (${ofFields.length}).`;
    }
    return "";
  };

  const handleConfirm = () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    const id = initialRule?.id || newRuleId();
    let rule;

    if (kind === RULE_KINDS.CONDITIONAL_REQUIRED) {
      rule = {
        id,
        kind,
        when: { field: whenField, equals: whenEquals },
        require: [...requireFields],
      };
    } else {
      rule = {
        id,
        kind,
        of: [...ofFields],
        at_least: atLeast,
      };
    }

    if (message.trim()) rule.message = message.trim();

    onConfirm(rule);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {isEditing ? "Edit validation rule" : "Add validation rule"}
          </h3>
          <button
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Kind picker — disabled when editing an existing rule.
              Changing the kind of an existing rule in place would
              leave stale kind-specific fields on the object; the
              admin should delete and re-create if they need a
              different shape. */}
          <div className={styles.field}>
            <label className={styles.label}>Rule type</label>
            <div className={styles.kindPicker}>
              <button
                type="button"
                className={`${styles.kindCard} ${
                  kind === RULE_KINDS.CONDITIONAL_REQUIRED
                    ? styles.kindCardActive
                    : ""
                }`}
                onClick={() => setKind(RULE_KINDS.CONDITIONAL_REQUIRED)}
                disabled={isEditing}
              >
                <div className={styles.kindCardTitle}>
                  {KIND_LABELS[RULE_KINDS.CONDITIONAL_REQUIRED]}
                </div>
                <div className={styles.kindCardDesc}>
                  A field is only required when another field has a
                  specific value.
                </div>
              </button>
              <button
                type="button"
                className={`${styles.kindCard} ${
                  kind === RULE_KINDS.PRESENCE_CARDINALITY
                    ? styles.kindCardActive
                    : ""
                }`}
                onClick={() => setKind(RULE_KINDS.PRESENCE_CARDINALITY)}
                disabled={isEditing}
              >
                <div className={styles.kindCardTitle}>
                  {KIND_LABELS[RULE_KINDS.PRESENCE_CARDINALITY]}
                </div>
                <div className={styles.kindCardDesc}>
                  At least N of a set of fields must be filled in.
                </div>
              </button>
            </div>
          </div>

          {/* Kind-specific editor */}
          {kind === RULE_KINDS.CONDITIONAL_REQUIRED && (
            <>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label}>When field</label>
                  <select
                    className={styles.select}
                    value={whenField}
                    onChange={(e) => setWhenField(e.target.value)}
                  >
                    <option value="">-- pick a field --</option>
                    {availableFields.map((f) => (
                      <option key={f.field} value={f.field}>
                        {f.label || f.field}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>equals</label>
                  <input
                    className={styles.input}
                    type="text"
                    value={whenEquals}
                    onChange={(e) => setWhenEquals(e.target.value)}
                    placeholder='e.g. yes'
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Then require these fields
                </label>
                {availableFields.length === 0 ? (
                  <div className={styles.emptyHint}>
                    No fields in this form yet.
                  </div>
                ) : (
                  <div className={styles.multiPicker}>
                    {availableFields.map((f) => (
                      <label
                        key={f.field}
                        className={styles.multiItem}
                      >
                        <input
                          type="checkbox"
                          checked={requireFields.includes(f.field)}
                          onChange={() => toggleRequireField(f.field)}
                        />
                        <span>{f.label || f.field}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {kind === RULE_KINDS.PRESENCE_CARDINALITY && (
            <>
              <div className={styles.field}>
                <label className={styles.label}>
                  At least how many?
                </label>
                <input
                  className={styles.input}
                  type="number"
                  min={1}
                  value={atLeast}
                  onChange={(e) =>
                    setAtLeast(parseInt(e.target.value, 10) || 1)
                  }
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Of these fields
                </label>
                {availableFields.length === 0 ? (
                  <div className={styles.emptyHint}>
                    No fields in this form yet.
                  </div>
                ) : (
                  <div className={styles.multiPicker}>
                    {availableFields.map((f) => (
                      <label
                        key={f.field}
                        className={styles.multiItem}
                      >
                        <input
                          type="checkbox"
                          checked={ofFields.includes(f.field)}
                          onChange={() => toggleOfField(f.field)}
                        />
                        <span>{f.label || f.field}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <div className={styles.field}>
            <label className={styles.label}>
              Message (optional)
            </label>
            <input
              className={styles.input}
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Shown to the user when the rule fails."
            />
          </div>

          {error && <div className={styles.errorText}>{error}</div>}
        </div>

        <div className={styles.modalActions}>
          <button
            className={styles.cancelBtn}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={styles.confirmBtn}
            onClick={handleConfirm}
          >
            {isEditing ? "Save changes" : "Add rule"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN PANEL
// ============================================================

const ValidationRulesBuilder = ({
  rules = [],
  availableFields = [],
  onChange,
  readOnly = false,
  loading = false,
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const fieldsByKey = useMemo(() => {
    const m = {};
    for (const f of availableFields) {
      m[f.field] = f;
    }
    return m;
  }, [availableFields]);

  const availableFieldKeys = useMemo(
    () => new Set(availableFields.map((f) => f.field)),
    [availableFields],
  );

  const handleAdd = () => {
    setEditingRule(null);
    setEditorOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setEditorOpen(true);
  };

  const handleDelete = (rule) => {
    if (!window.confirm("Delete this validation rule?")) return;
    onChange(rules.filter((r) => r.id !== rule.id));
  };

  const handleConfirm = (rule) => {
    const existing = rules.find((r) => r.id === rule.id);
    const next = existing
      ? rules.map((r) => (r.id === rule.id ? rule : r))
      : [...rules, rule];
    onChange(next);
    setEditorOpen(false);
    setEditingRule(null);
  };

  const handleClose = () => {
    setEditorOpen(false);
    setEditingRule(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h4 className={styles.title}>Cross-field validation</h4>
          <p className={styles.subtitle}>
            Rules that check fields together — for example, "pet type
            is required when has_pet is yes," or "provide either an
            email or a phone number."
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            className={styles.addBtn}
            onClick={handleAdd}
            disabled={loading || availableFields.length === 0}
            title={
              availableFields.length === 0
                ? "Add fields to the form first."
                : ""
            }
          >
            <FaPlus size={11} />
            Add rule
          </button>
        )}
      </div>

      {loading && (
        <div className={styles.loadingHint}>Loading rules…</div>
      )}

      {!loading && rules.length === 0 && (
        <div className={styles.empty}>
          {availableFields.length === 0
            ? "Add fields to the form before adding cross-field rules."
            : "No cross-field rules yet. Click Add rule to create one."}
        </div>
      )}

      {!loading && rules.length > 0 && (
        <div className={styles.list}>
          {rules.map((rule) => {
            const stale = findStaleFieldRefs(rule, availableFieldKeys);
            return (
              <div key={rule.id} className={styles.ruleRow}>
                <div className={styles.ruleBody}>
                  <div className={styles.ruleSummary}>
                    {summariseRule(rule, fieldsByKey)}
                  </div>
                  {rule.message && (
                    <div className={styles.ruleMessage}>
                      “{rule.message}”
                    </div>
                  )}
                  {stale.length > 0 && (
                    <div className={styles.ruleWarning}>
                      ⚠️ References fields that no longer exist:{" "}
                      {stale.join(", ")}
                    </div>
                  )}
                </div>
                {!readOnly && (
                  <div className={styles.ruleActions}>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => handleEdit(rule)}
                      title="Edit rule"
                    >
                      <FaEdit size={12} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      onClick={() => handleDelete(rule)}
                      title="Delete rule"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <RuleEditorModal
        isOpen={editorOpen}
        initialRule={editingRule}
        availableFields={availableFields}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default ValidationRulesBuilder;